import React, { useEffect, useState } from 'react';

function PostStats({ slug }) {
    const [totalViews, setTotalViews] = useState(0);
    const [uniqueViews, setUniqueViews] = useState(0);
    const [likes, setLikes] = useState(0);
    const [hasLiked, setHasLiked] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Function to detect web crawlers
    const isWebCrawler = () => {
        const userAgent = navigator.userAgent.toLowerCase();
        const crawlers = [
            'googlebot',
            'bingbot',
            'yandex',
            'baiduspider',
            'facebookexternalhit',
            'twitterbot',
            'rogerbot',
            'linkedinbot',
            'embedly',
            'quora link preview',
            'showyoubot',
            'outbrain',
            'pinterest',
            'slackbot',
            'vkShare',
            'W3C_Validator',
            'crawler',
            'spider',
            'robot',
            'bot'
        ];
        return crawlers.some(crawler => userAgent.includes(crawler));
    };

    // Function to fetch stats
    const fetchStats = async () => {
        try {
            const response = await fetch(`/api/stats/${slug}`);
            if (!response.ok) throw new Error('Failed to fetch stats');
            const data = await response.json();
            setTotalViews(data.totalViews || 0);
            setUniqueViews(data.uniqueViews || 0);
            setLikes(data.likes || 0);
        } catch (err) {
            console.error('Error fetching stats:', err);
            setError('Failed to load stats');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!slug) {
            setError('No post slug provided');
            setIsLoading(false);
            return;
        }

        // Skip view counting for web crawlers
        if (isWebCrawler()) {
            setIsLoading(false);
            return;
        }

        // Check if this is a unique view
        const viewedPosts = JSON.parse(localStorage.getItem('viewedPosts') || '{}');
        const isUniqueView = !viewedPosts[slug];

        // Update view counts
        const updateViews = async () => {
            try {
                // Update total views
                const totalViewsResponse = await fetch(`/api/stats/view`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ slug })
                });
                
                if (!totalViewsResponse.ok) throw new Error('Failed to update total views');

                // Update unique views if this is a unique view
                if (isUniqueView) {
                    const uniqueViewsResponse = await fetch(`/api/stats/unique-view`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ slug })
                    });

                    if (!uniqueViewsResponse.ok) throw new Error('Failed to update unique views');
                    
                    // Mark as viewed in localStorage
                    viewedPosts[slug] = true;
                    localStorage.setItem('viewedPosts', JSON.stringify(viewedPosts));
                }
            } catch (err) {
                console.error('Error updating views:', err);
                setError('Failed to update views');
            }
        };

        // Initial fetch and view update
        fetchStats();
        updateViews();

        // Set up polling for real-time updates
        const pollInterval = setInterval(fetchStats, 30000); // Poll every 30 seconds

        // Check if user has liked the post
        const likedPosts = JSON.parse(localStorage.getItem('likedPosts') || '{}');
        setHasLiked(!!likedPosts[slug]);

        return () => clearInterval(pollInterval);
    }, [slug]);

    const handleLike = async () => {
        if (!hasLiked) {
            try {
                const response = await fetch(`/api/stats/like`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ slug })
                });

                if (!response.ok) throw new Error('Failed to update likes');
                
                // Mark as liked in localStorage
                const likedPosts = JSON.parse(localStorage.getItem('likedPosts') || '{}');
                likedPosts[slug] = true;
                localStorage.setItem('likedPosts', JSON.stringify(likedPosts));
                
                setHasLiked(true);
                setLikes(prev => prev + 1);
            } catch (error) {
                console.error('Error updating likes:', error);
                setError('Failed to update likes');
            }
        }
    };

    if (isLoading) {
        return <div className="post-stats loading">Loading...</div>;
    }

    if (error) {
        return <div className="post-stats error">{error}</div>;
    }

    return (
        <div className="post-stats">
            <div className="stat-item">
                <span className="stat-icon">👁️</span>
                <span className="stat-value">{totalViews}</span>
            </div>
            <div className="stat-item">
                <span className="stat-icon">👤</span>
                <span className="stat-value">{uniqueViews}</span>
            </div>
            <button 
                className={`like-button ${hasLiked ? 'liked' : ''}`}
                onClick={handleLike}
                disabled={hasLiked}
            >
                <span className="stat-icon">{hasLiked ? '❤️' : '🤍'}</span>
                <span className="stat-value">{likes}</span>
            </button>
        </div>
    );
}

export default PostStats; 